import { EyeIcon, MinusCircleIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { ColDef } from 'ag-grid-community';
import {
  InvitationActionLinks,
  InvitationStatusPill,
} from '~/components/agGrid/columns/invitationColumns';
import { ACTIONS_COLUMN_BASE } from '~/components/Table/gridUtil';
import { PeerReference } from '~/types';
import { CreateRefColumnsProps } from './types';

const TAGS = CT.peerRefReq;

// Parse YYYY-MM-DD as local midnight. `new Date('2026-04-13')` parses as UTC
// midnight, which becomes the previous day in negative UTC offsets (e.g. Pacific)
// and renders as yesterday's date via toLocaleDateString.
export const formatRequestDate = (value: string | null | undefined): string => {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    const [, y, m, d] = match;
    return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString();
  }
  return new Date(value).toLocaleDateString();
};

export const createRefColDefs = (props: CreateRefColumnsProps): ColDef<PeerReference>[] => {
  const { handleDownload, handleDelete, handleResend, resendDisabled } = props;

  return [
    {
      headerName: 'Name',
      field: 'lastName',
      sortable: true,
      valueGetter: ({ data }) => `${data?.lastName}, ${data?.firstName}`,
    },
    {
      headerName: 'Email',
      field: 'emailAddress',
      sortable: true,
    },
    {
      headerName: 'Relationship',
      field: 'relationshipType',
      sortable: true,
    },
    {
      headerName: 'Date',
      field: 'requestDate',
      sortable: true,
      valueFormatter: ({ value }) => formatRequestDate(value),
    },
    {
      headerName: 'Status',
      field: 'referenceDate',
      sortable: true,
      // [MCV-1872] Same pill as the employer invitation grids ('Pending' replaces
      // 'Sent'). The text filter reads filterValueGetter, not the formatter, so
      // without it the filter matched the raw reference date and 'Pending' or
      // 'Received' found nothing.
      valueFormatter: ({ value }) => (value ? 'Received' : 'Pending'),
      filterValueGetter: ({ data }) => (data?.referenceDate ? 'Received' : 'Pending'),
      cellRenderer: ({ data }: { data?: PeerReference }) =>
        data ? <InvitationStatusPill status={data.referenceDate ? 'RECEIVED' : 'PENDING'} /> : null,
    },
    {
      // [MCV-1405] Pinning, locking, [MCV-1750] resizable:false and filter:false
      // come from ACTIONS_COLUMN_BASE (components/Table/gridUtil.tsx).
      ...(ACTIONS_COLUMN_BASE as ColDef<PeerReference>),
      // [MCV-1405 round 2] Pinning right removes this column from ag-grid's
      // flexed center-column set (refreshFlexedColumns only flexes
      // visibleCols.centerCols), so the inherited flex:1 from BasicGrid's
      // defaultColDef was silently ignored and the column fell back to
      // ag-grid's 200px default -- squeezing the 5 data columns. The base's
      // flex:0 plus an explicit width (fitted to the cellRenderer below, at most
      // 2 controls: View + delete, or Resend + Revoke, see
      // ActionsColumnPinning.browser.spec.tsx) keeps the size fixed and intentional.
      width: 150,
      cellRenderer: ({ data, rowIndex }: { data: PeerReference; rowIndex: number }) => {
        const responseReceived = Boolean(data.referenceDate);
        // [MCV-1852] The server decides who may see the answers, so the eye
        // follows what it sent: the list masks formInstance when the viewer
        // may not read it. shareFormFlag is the reviewer's choice for the
        // PROVIDER only; an employer acting as the provider gets every
        // received reference's answers, so gating on the flag would hide them.
        const responsesShared = responseReceived && Boolean(data.formInstance);
        // MCV-1751: no separate view/details action. Answers are reachable only
        // through the eye below, which opens the responses and renders only
        // when the API sent them (formInstance, see above). The PDF endpoint
        // enforces the same rule and returns 401 otherwise.

        return (
          <div className="flex items-center justify-end gap-4">
            {/* [MCV-1838] Only a still-pending request can be re-sent.
                [MCV-1872] Revoke sits beside it as a text link, like the
                employer invitation grids. The API hard-deletes a pending
                request, so Revoke calls handleDelete and the link stops
                working. Revoke is never disabled by an in-flight resend. */}
            {!responseReceived && data.id && (
              <InvitationActionLinks
                onResend={() => handleResend(data.id, data.emailAddress)}
                onRevoke={() => handleDelete(data.id, rowIndex)}
                resendDisabled={resendDisabled}
                resendCyTag={TAGS.table.resendBtn + rowIndex}
                revokeCyTag={TAGS.table.deleteBtn + rowIndex}
              />
            )}
            {responsesShared && data.id && (
              <button
                type="button"
                aria-label="View peer reference responses"
                title="View peer reference responses"
                data-cy={TAGS.table.download + rowIndex}
                onClick={() => handleDownload(data.id, rowIndex)}
                className="text-grey-100 hover:text-primary-50 cursor-pointer"
              >
                <EyeIcon className="size-5 shrink-0" />
              </button>
            )}
            {responseReceived && data.id && (
              <MinusCircleIcon
                data-cy={TAGS.table.deleteBtn + rowIndex}
                onClick={() => handleDelete(data.id, rowIndex)}
                className="size-5 shrink-0 text-grey-100 hover:text-primary-50 cursor-pointer"
              />
            )}
          </div>
        );
      },
    },
  ];
};

export const PEER_RELATIONSHIP: PeerReference['relationshipType'][] = [
  'Colleague',
  'Supervisor or Manager',
  'Direct Report',
  'Client | Customer',
  'Academic Relationship',
  'Program Director',
  'Academic Contact',
  'Mentor',
  'Community Leader',
];

export const PEER_RELATIONSHIP_OPTIONS = PEER_RELATIONSHIP.map((rel) => {
  return { value: rel, label: rel };
});

export const emptyRequestForm = {
  firstName: '',
  lastName: '',
  emailAddress: '',
  phoneNum: '',
  relationshipType: PEER_RELATIONSHIP_OPTIONS[0],
};
